import { useAuth } from "@clerk/clerk-react";
import { AnimatePresence, motion } from "framer-motion";
import { Fragment, useEffect, useRef, useState } from "react";
import { ListIcon, PlusIcon } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import { ButtonType, MaxWidthType } from "../../@types";

import { Icon } from "@/components/atoms/Icon";
import { Logo } from "@/components/atoms/Logo";
import { icons } from "@/objects/@icons";
import { Navigation } from "@/constants";
import { useDevice } from "@/hooks/useDevice";
import { DropdownType } from "@/layouts/@types";
import { Dropdown } from "@/layouts/PublicLayout/PublicTopbar/Dropdown";
import { Button } from "@/components/ui/button";
import { cn } from "@/components/ui/utils";

export function PublicTopbar({
  navigationButtons,
  actionButtons,
  maxWidth = "md",
}: {
  navigationButtons?: {
    component: ButtonType;
    dropdown: DropdownType[];
  }[];
  actionButtons?: ButtonType[];
  maxWidth?: MaxWidthType;
}) {
  const { isSignedIn } = useAuth();
  const navigate = useNavigate();
  const [dropdownIdx, setDropdownIdx] = useState<{
    idx: number | null;
    prevIdx: number | null;
  }>({
    idx: null,
    prevIdx: null,
  });

  const navButtonRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { isWide } = useDevice();
  const [mobileDropdown, setMobileDropdown] = useState(false);

  const [dropdownPosition, setDropdownPosition] = useState({
    top: "-8px",
    left: "0px",
  });
  const closeDropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const HOVER_CLOSE_DELAY_MS = 50;

  const clearDropdownCloseTimeout = () => {
    if (closeDropdownTimeoutRef.current) {
      clearTimeout(closeDropdownTimeoutRef.current);
      closeDropdownTimeoutRef.current = null;
    }
  };

  const scheduleDropdownClose = () => {
    clearDropdownCloseTimeout();
    closeDropdownTimeoutRef.current = setTimeout(() => {
      setDropdownIdx({
        idx: null,
        prevIdx: null,
      });
    }, HOVER_CLOSE_DELAY_MS);
  };

  useEffect(() => {
    if (dropdownIdx.idx === null) return;
    const activeButton = navButtonRefs.current[dropdownIdx.idx];
    if (!activeButton) return;
    const rect = activeButton.getBoundingClientRect();
    setDropdownPosition({
      top: "-8px",
      left: `${rect.left}px`,
    });
    return () => {
      clearDropdownCloseTimeout();
    };
  }, [dropdownIdx.idx]);

  let determinedAnimationDirection: "rtl" | "ltr" | "default" = "default";
  if (dropdownIdx.idx !== null && dropdownIdx.prevIdx !== null) {
    if (
      dropdownIdx.prevIdx !== null &&
      dropdownIdx.prevIdx !== dropdownIdx.idx
    ) {
      if (dropdownIdx.idx > dropdownIdx.prevIdx) {
        determinedAnimationDirection = "rtl";
      } else {
        determinedAnimationDirection = "ltr";
      }
    }
  }

  return (
    <>
      <div className="relative flex flex-row items-center flex-nowrap min-h-16 max-h-16 w-full justify-center h-full z-1000000">
        {!isWide && (
          <div className="flex flex-row items-center justify-between w-full h-full pl-4 pr-4 md:pl-8 md:pr-8">
            <div
              className="flex items-center cursor-pointer"
              onClick={() => {
                const urlPath = new URL(window.location.href).pathname;
                if (isSignedIn && urlPath.includes("app")) {
                  navigate(Navigation.ROUTES.APP.PAGE);
                } else {
                  navigate(Navigation.ROUTES.PAGE);
                }
              }}
            >
              <Logo variant="logo-only" size="lg" />
            </div>
            <div
              className="flex flex-col h-full justify-center items-center relative cursor-pointer ml-auto"
              onClick={() => {
                setMobileDropdown(!mobileDropdown);
              }}
            >
              {!mobileDropdown ? (
                <ListIcon size={32} />
              ) : (
                <PlusIcon
                  size={32}
                  style={{
                    transform: "rotate(45deg)",
                  }}
                />
              )}
              {mobileDropdown && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="fixed top-16 left-0 right-0 bottom-0 z-1000 w-screen h-[calc(100vh-64px)] flex flex-col bg-muted [border-top-width:1px] [border-top-style:solid] border-t-border p-4 gap-4 overflow-y-auto"
                >
                  {actionButtons?.map((actionButton) => {
                    return (
                      <Fragment key={actionButton.name}>
                        <Button variant="secondary" size="lg" asChild>
                          <Link to={actionButton.path}>
                            {actionButton.name}
                          </Link>
                        </Button>
                      </Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {isWide && (
          <>
            <div
              className="flex gap-4 grow relative justify-start items-center flex-nowrap min-h-16 max-h-16 w-full pl-6 pr-6 lg:pl-8 lg:pr-8 xl:pl-12 xl:pr-12"
              style={{
                maxWidth:
                  {
                    xs: 0,
                    sm: 400,
                    md: 720,
                    lg: 1000,
                    xl: 1400,
                  }[maxWidth] + "px",
              }}
            >
              <div
                onClick={() => {
                  const urlPath = new URL(window.location.href).pathname;
                  if (isSignedIn && urlPath.includes("app")) {
                    navigate(Navigation.ROUTES.APP.PAGE);
                  } else {
                    navigate(Navigation.ROUTES.PAGE);
                  }
                }}
                className="cursor-pointer"
              >
                <Logo variant="logo-only" />
              </div>
              <div className="flex gap-2 p-4 z-10000">
                {navigationButtons?.map((button, idx) => {
                  const { component } = button;
                  return (
                    <Fragment key={button.component.name}>
                      <div
                        ref={(el: HTMLDivElement | null) => {
                          navButtonRefs.current[idx] = el;
                        }}
                        onMouseEnter={() => {
                          if (button.dropdown && button.dropdown.length > 0) {
                            clearDropdownCloseTimeout();
                            setDropdownIdx({
                              idx: idx,
                              prevIdx: dropdownIdx.idx,
                            });
                          }
                        }}
                        onMouseLeave={() => {
                          if (button.dropdown && button.dropdown.length > 0) {
                            scheduleDropdownClose();
                          }
                        }}
                      >
                        <Button
                          variant="ghost"
                          {...(button.component.path
                            ? {
                                component: Link,
                                to: button.component.path,
                              }
                            : {})}
                          onClick={(event) => {
                            if (button.dropdown && button.dropdown.length > 0) {
                              event.preventDefault();
                              if (dropdownIdx.idx === idx) {
                                setDropdownIdx({
                                  idx: null,
                                  prevIdx: dropdownIdx.idx,
                                });
                                clearDropdownCloseTimeout();
                              } else {
                                setDropdownIdx({
                                  idx: idx,
                                  prevIdx: dropdownIdx.idx,
                                });
                                clearDropdownCloseTimeout();
                              }
                            } else if (dropdownIdx.idx !== null) {
                              setDropdownIdx({
                                idx: null,
                                prevIdx: dropdownIdx.idx,
                              });
                              clearDropdownCloseTimeout();
                            }
                          }}
                          className={cn(
                            "pl-3 pr-3",
                            dropdownIdx.idx === idx && "bg-accent",
                          )}
                        >
                          {component.name}
                          {button.dropdown != null &&
                          button.dropdown.length > 0 ? (
                            <>
                              <Icon ReactIcon={icons.chevronDown} size={"sm"} />
                            </>
                          ) : null}
                        </Button>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
              <div className="flex items-center grow" />
              <div className="flex items-center gap-4">
                {actionButtons?.map((button, idx) => {
                  return (
                    <Fragment key={button.name}>
                      <Button
                        asChild
                        variant={
                          idx === actionButtons.length - 1
                            ? "default"
                            : "outline"
                        }
                        className="pl-4 pr-4"
                      >
                        <Link to={button.path}>{button.name}</Link>
                      </Button>
                    </Fragment>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
      <div className="z-999990 w-full sticky top-0 h-fit">
        <AnimatePresence mode="wait">
          {dropdownIdx.idx !== null &&
            navigationButtons &&
            navigationButtons[dropdownIdx.idx] && (
              <motion.div
                key="dropdown-panel"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                style={{
                  position: "absolute",
                  top: dropdownPosition.top,
                  left: dropdownPosition.left,

                  zIndex: 1,
                }}
              >
                <Dropdown
                  dropdown={navigationButtons[dropdownIdx.idx].dropdown}
                  dropdownKey={dropdownIdx.idx}
                  clearCloseTimeout={clearDropdownCloseTimeout}
                  scheduleCloseDropdown={scheduleDropdownClose}
                  onItemSelect={() => {
                    clearDropdownCloseTimeout();
                    setDropdownIdx({ idx: null, prevIdx: dropdownIdx.idx });
                  }}
                  animationDirection={determinedAnimationDirection}
                />
              </motion.div>
            )}
        </AnimatePresence>
      </div>
    </>
  );
}
